// Sound effects using Web Audio API
type SoundType = 
  | 'pop' | 'match' | 'combo' | 'superCombo' 
  | 'wrong' | 'win' | 'lose' | 'levelUp'
  | 'tick' | 'go' | 'click' | 'whoosh'
  | 'coin' | 'powerup' | 'explosion'

let audioContext: AudioContext | null = null
let audioInitialized = false

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
  }
  return audioContext
}

// Initialize audio on first user interaction (required for iOS/mobile)
export const initAudio = () => {
  if (audioInitialized) return
  
  try {
    const ctx = getAudioContext()
    
    // Resume suspended context (required for iOS Safari)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    
    // Play silent sound to unlock audio
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    gainNode.gain.value = 0.001 // Nearly silent
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    oscillator.start()
    oscillator.stop(ctx.currentTime + 0.001)
    
    audioInitialized = true
  } catch (e) {
    // Silently fail
  }
}

// Auto-init on any user interaction
if (typeof window !== 'undefined') {
  const initOnInteraction = () => {
    initAudio()
    // Remove listeners after first init
    window.removeEventListener('touchstart', initOnInteraction)
    window.removeEventListener('touchend', initOnInteraction)
    window.removeEventListener('click', initOnInteraction)
    window.removeEventListener('keydown', initOnInteraction)
  }
  
  window.addEventListener('touchstart', initOnInteraction, { passive: true })
  window.addEventListener('touchend', initOnInteraction, { passive: true })
  window.addEventListener('click', initOnInteraction, { passive: true })
  window.addEventListener('keydown', initOnInteraction, { passive: true })
}

export const playSound = (type: SoundType, volume = 0.3) => {
  try {
    const ctx = getAudioContext()
    
    // Resume if suspended (for iOS)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    
    const now = ctx.currentTime
    gainNode.gain.setValueAtTime(volume, now)

    switch (type) {
      case 'pop':
        // Quick pop sound
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(600, now)
        oscillator.frequency.exponentialRampToValueAtTime(200, now + 0.1)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1)
        oscillator.start(now)
        oscillator.stop(now + 0.1)
        break

      case 'match':
        // Satisfying match sound - ascending notes
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(440, now)
        oscillator.frequency.setValueAtTime(554, now + 0.08)
        oscillator.frequency.setValueAtTime(659, now + 0.16)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.25)
        oscillator.start(now)
        oscillator.stop(now + 0.25)
        break

      case 'combo':
        // Exciting combo - faster ascending
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(523, now)
        oscillator.frequency.setValueAtTime(659, now + 0.05)
        oscillator.frequency.setValueAtTime(784, now + 0.1)
        oscillator.frequency.setValueAtTime(1047, now + 0.15)
        gainNode.gain.setValueAtTime(volume * 1.2, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3)
        oscillator.start(now)
        oscillator.stop(now + 0.3)
        break

      case 'superCombo':
        // Epic super combo - arpeggio
        oscillator.type = 'square'
        oscillator.frequency.setValueAtTime(523, now)
        oscillator.frequency.setValueAtTime(659, now + 0.05)
        oscillator.frequency.setValueAtTime(784, now + 0.1)
        oscillator.frequency.setValueAtTime(1047, now + 0.15)
        oscillator.frequency.setValueAtTime(1318, now + 0.2)
        gainNode.gain.setValueAtTime(volume * 1.5, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4)
        oscillator.start(now)
        oscillator.stop(now + 0.4)
        break

      case 'wrong':
        // Error buzz
        oscillator.type = 'sawtooth'
        oscillator.frequency.setValueAtTime(150, now)
        oscillator.frequency.setValueAtTime(100, now + 0.1)
        gainNode.gain.setValueAtTime(volume * 0.5, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15)
        oscillator.start(now)
        oscillator.stop(now + 0.15)
        break

      case 'win':
        // Victory fanfare
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(523, now)
        oscillator.frequency.setValueAtTime(659, now + 0.12)
        oscillator.frequency.setValueAtTime(784, now + 0.24)
        oscillator.frequency.setValueAtTime(1047, now + 0.36)
        oscillator.frequency.setValueAtTime(1318, now + 0.48)
        gainNode.gain.setValueAtTime(volume, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.6)
        oscillator.start(now)
        oscillator.stop(now + 0.6)
        break

      case 'lose':
        // Sad descending
        oscillator.type = 'triangle'
        oscillator.frequency.setValueAtTime(400, now)
        oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.5)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5)
        oscillator.start(now)
        oscillator.stop(now + 0.5)
        break

      case 'levelUp':
        // Level up celebration
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(440, now)
        oscillator.frequency.setValueAtTime(554, now + 0.1)
        oscillator.frequency.setValueAtTime(659, now + 0.2)
        oscillator.frequency.setValueAtTime(880, now + 0.3)
        oscillator.frequency.setValueAtTime(1108, now + 0.4)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5)
        oscillator.start(now)
        oscillator.stop(now + 0.5)
        break

      case 'tick':
        // Timer tick
        oscillator.type = 'sine'
        oscillator.frequency.value = 800
        gainNode.gain.setValueAtTime(volume * 0.8, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05)
        oscillator.start(now)
        oscillator.stop(now + 0.05)
        break

      case 'go':
        // Go signal
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(440, now)
        oscillator.frequency.setValueAtTime(880, now + 0.1)
        gainNode.gain.setValueAtTime(volume, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2)
        oscillator.start(now)
        oscillator.stop(now + 0.2)
        break

      case 'click':
        // UI click
        oscillator.type = 'sine'
        oscillator.frequency.value = 1000
        gainNode.gain.setValueAtTime(volume * 0.5, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.03)
        oscillator.start(now)
        oscillator.stop(now + 0.03)
        break

      case 'whoosh':
        // Shuffle/swoosh
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(1200, now)
        oscillator.frequency.exponentialRampToValueAtTime(200, now + 0.15)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15)
        oscillator.start(now)
        oscillator.stop(now + 0.15)
        break

      case 'coin':
        // Coin/resource collect
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(987, now)
        oscillator.frequency.setValueAtTime(1318, now + 0.08)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15)
        oscillator.start(now)
        oscillator.stop(now + 0.15)
        break

      case 'powerup':
        // Power up
        oscillator.type = 'square'
        oscillator.frequency.setValueAtTime(200, now)
        oscillator.frequency.exponentialRampToValueAtTime(800, now + 0.15)
        gainNode.gain.setValueAtTime(volume * 0.7, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2)
        oscillator.start(now)
        oscillator.stop(now + 0.2)
        break

      case 'explosion':
        // Explosion/bomb
        oscillator.type = 'sawtooth'
        oscillator.frequency.setValueAtTime(100, now)
        oscillator.frequency.exponentialRampToValueAtTime(30, now + 0.3)
        gainNode.gain.setValueAtTime(volume * 0.8, now)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3)
        oscillator.start(now)
        oscillator.stop(now + 0.3)
        break
    }
  } catch (e) {
    // Silently fail if audio not supported
  }
}

// Haptic feedback (for mobile)
export const vibrate = (pattern: number | number[] = 50) => {
  try {
    if (navigator.vibrate) {
      navigator.vibrate(pattern)
    }
  } catch (e) {
    // Silently fail
  }
}

// Combined feedback
export const feedback = (type: SoundType, vibrateMs = 50) => {
  playSound(type)
  vibrate(vibrateMs)
}
